import { CommunityId, EvergreenCommunityListingAvailability } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import {
    FindOrCreateCommunityInsightsMutation,
    ObservationKind,
} from "generated/graphql";
import React, { useCallback } from "react";
import { useCanRequestCommunityAvailability } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { CommunityRequestListingSignupPanel } from "./community-request-listing-signup-panel";
import { CommunityRequestPricingUpdatePanel } from "./community-request-pricing-update-panel";

interface CommunityPricingMissingProps {
    listing: FindOrCreateCommunityInsightsMutation["findOrCreateCommunityInsights"];
    communityId: CommunityId;
}

export const CommunityPricingMissing = (props: CommunityPricingMissingProps) => {
    const { listing, communityId } = props;
    const requestUpdatesAllowed = useCanRequestCommunityAvailability();
    const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
    const [isRequestParticipationDialogOpen, setIsRequestParticipationDialogOpen] =
        React.useState(false);
    const [hasRequestedPricing, setHasRequestedPricing] = React.useState(false);

    const hasAvailability =
        !!listing.availability?.status &&
        listing.availability?.status !== EvergreenCommunityListingAvailability.Unknown;

    const requestPricing = useCallback(() => {
        handleEvent("request-community-pricing", {
            communityId,
            listingId: listing.id,
        });
        setIsRequestDialogOpen(true);
    }, [communityId, listing.id]);

    const requestParticipation = useCallback(() => {
        handleEvent("request-community-pricing-signup", {
            communityId,
            listingId: listing.id,
        });
        setIsRequestParticipationDialogOpen(true);
    }, [communityId, listing.id]);

    const closeRequestDialog = useCallback(() => {
        setIsRequestDialogOpen(false);
    }, []);

    const closeRequestParticipationDialog = useCallback(() => {
        setIsRequestParticipationDialogOpen(false);
    }, []);

    const onRequestSuccess = useCallback(() => {
        setHasRequestedPricing(true);
        closeRequestDialog();
        handleEvent("request-community-pricing-success", {
            communityId,
            listingId: listing.id,
        });
    }, [closeRequestDialog, communityId, listing.id]);

    const onRequestParticipationSuccess = useCallback(() => {
        setHasRequestedPricing(true);
        closeRequestParticipationDialog();
        handleEvent("request-community-pricing-signup-success", {
            communityId,
            listingId: listing.id,
        });
    }, [closeRequestParticipationDialog, communityId, listing.id]);

    const canRequestPricing = requestUpdatesAllowed && hasAvailability;
    const canRequestParticipation = requestUpdatesAllowed && !hasAvailability;

    return (
        <>
            {isRequestDialogOpen ? (
                <CommunityRequestPricingUpdatePanel
                    communityId={communityId}
                    communityName={listing.name}
                    onDismiss={closeRequestDialog}
                    onSuccess={onRequestSuccess}
                />
            ) : null}
            {isRequestParticipationDialogOpen ? (
                <CommunityRequestListingSignupPanel
                    communityId={communityId}
                    onDismiss={closeRequestParticipationDialog}
                    onSuccess={onRequestParticipationSuccess}
                    observationFocus={ObservationKind.Pricing}
                />
            ) : null}
            <div className="space-y-4 mt-2">
                <div className="text-sm text-gray-500">
                    {listing.name} has not yet shared pricing information with Community
                    Insights.
                </div>
                {canRequestPricing ? (
                    <div>
                        <PrimaryButton onClick={requestPricing}>
                            Request Pricing
                        </PrimaryButton>
                    </div>
                ) : null}
                {canRequestParticipation ? (
                    <div>
                        <PrimaryButton onClick={requestParticipation}>
                            Request Pricing
                        </PrimaryButton>
                    </div>
                ) : null}
                {hasRequestedPricing ? (
                    <InlineBanner type="info">Pricing request sent!</InlineBanner>
                ) : null}
            </div>
        </>
    );
};
